import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useState } from 'react';
import { Plus } from 'lucide-react';
import ModulePageHeading from '../../components/ModulePageHeading';
import { resolveNetworkNav } from '../../navigation/networkNavigation';
import { useAuthStore } from '../../stores/authStore';
import OurPartnerTab from './OurPartnerTab';
import PartlyAgentTab from './PartlyAgentTab';
import AgentTab from './AgentTab';
import IqpTab from './IqpTab';
import EngineersTab from './EngineersTab';

const SECTION_TABS = new Set(['our-partner', 'partly-agent', 'agent', 'iqp', 'engineers']);

export default function ServiceNetwork() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const activeSection = SECTION_TABS.has(tab) ? tab : 'overview';
  const [iqpHeaderActions, setIqpHeaderActions] = useState(null);
  const onIqpHeaderActions = useCallback((node) => setIqpHeaderActions(node), []);

  if (user?.role === 'WORKER') {
    return <Navigate to="/" replace />;
  }

  const headerActions =
    activeSection === 'our-partner' ? (
      <button
        type="button"
        onClick={() => navigate('/network/partners/new')}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-white text-navy-900 hover:bg-white/90"
      >
        <Plus className="w-4 h-4" />
        Register partner
      </button>
    ) : activeSection === 'engineers' ? (
      <Link
        to="/experts/new"
        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-white text-navy-900 hover:bg-white/90"
      >
        <Plus className="w-4 h-4" />
        Add engineer
      </Link>
    ) : activeSection === 'iqp' ? (
      iqpHeaderActions
    ) : null;

  return (
    <div>
      <ModulePageHeading
        resolveNav={resolveNetworkNav}
        title="Registered Company"
        infoTitle="Registered Company"
        infoChildren={
          <p>
            Directory of external companies and people used for SS inspection, mandates, and compliance amendments. Use
            the header sub-menu in the order below.
          </p>
        }
        actions={headerActions}
      />

      {activeSection === 'overview' && (
        <div className="bg-card rounded-lg shadow border border-gray-200 p-6 max-w-3xl space-y-6">
          <section>
            <h2 className="text-lg font-semibold text-gray-900">Role of this menu</h2>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">
              Registered Company is the master directory for parties outside your office who take part in BWOF work:
              inspection partners, network organisations, council-registered IQPs, and amendment engineers. Building
              service mandates, Form 12A partner details, and Application expert requests draw from these lists. Keep
              this directory up to date before assigning work on a building or starting an amendment.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900">Suggested order</h2>
            <ol className="mt-3 space-y-3 text-sm text-gray-700 list-decimal list-inside leading-relaxed">
              <li>
                <strong>Our Partner</strong> — Companies you engage to inspect SS on your buildings. Register from Form
                12A or manually; used for service mandates and partner history.
              </li>
              <li>
                <strong>Partly agent</strong> — External Agent companies used when a building’s coverage is Partial.
                Pick one under Owner on Add/Edit Building, or type a new company there. Paperwork hand-off uses it.
              </li>
              <li>
                <strong>Agent</strong> — Network / supplier organisations linked to buildings. Maintain contacts and
                company records that sit behind partner and mandate workflows.
              </li>
              <li>
                <strong>Registered IQP</strong> — Council IQP directory (Form 12A, CSV, or manual entry). Reference for
                who is licensed to certify; not the same as your contracted Our Partner list.
              </li>
              <li>
                <strong>Engineers</strong> — Compliance schedule amendment experts (PS1 / CoA). Used when Application →
                Amend CS List needs expert assignment.
              </li>
            </ol>
            <p className="mt-4 text-sm text-gray-500">
              Open each section from the header sub-menu (Overview · Our Partner · Partly agent · Agent · Registered
              IQP · Engineers).
            </p>
          </section>
        </div>
      )}

      {(activeSection === 'iqp' || activeSection === 'engineers') && (
        <p className="text-sm text-gray-500 mb-6">
          {activeSection === 'iqp' &&
            'Council-registered IQPs. Data from Form 12A, CSV, or manual entry — not from Looker Studio API.'}
          {activeSection === 'engineers' &&
            'Compliance schedule amendment experts (PS1 / CoA). Separate from registered IQPs.'}
        </p>
      )}

      {activeSection === 'our-partner' && <OurPartnerTab />}
      {activeSection === 'partly-agent' && <PartlyAgentTab />}
      {activeSection === 'agent' && <AgentTab />}
      {activeSection === 'iqp' && <IqpTab onHeaderActions={onIqpHeaderActions} />}
      {activeSection === 'engineers' && <EngineersTab />}
    </div>
  );
}
